/**
 * The confirmation a date change raises on an edited composition (MDOT-25191, AC-6).
 *
 * The control exists in quantity mode alone, so the whole condition is that the composition is
 * dirty: the user has entered quantities, and a reprice rewrites every market value and weight
 * under them. It says so and asks; it performs nothing, so cancelling leaves the date, the URL and
 * the grid exactly as they were.
 */
import type { MinimumDialogProps } from "$root/components/spawnable/type";
import { useLocaleFormatters } from "$root/localization/hooks";
import { Dialog, DialogFooter, Text } from "@mdotm/shadcn/legacy/components";
import { Button, SubmitButton } from "@mdotm/shadcn/mdotm/components";
import { type SpawnResult, adaptAnimatedNodeProvider, spawn } from "@mdotm/shadcn/legacy/react-extensions";
import { useTranslation } from "react-i18next";

type RepriceConfirmDialogProps = {
	/** The day the composition would be priced at (`YYYY-MM-DD`). */
	date: string;
	onConfirm(): void;
};

function RepriceConfirmDialog({
	show,
	onClose,
	onAnimationStateChange,
	date,
	onConfirm,
}: RepriceConfirmDialogProps & MinimumDialogProps) {
	const { t } = useTranslation();
	const { formatDate } = useLocaleFormatters();

	return (
		<Dialog
			show={show}
			onClose={onClose}
			header={t("EDIT_COMPOSITION.REFERENCE_DATE.REPRICE_CONFIRM.TITLE")}
			onAnimationStateChange={onAnimationStateChange}
			footer={
				<DialogFooter
					neutralAction={
						<Button palette="tertiary" onClick={onClose} data-qualifier="CompositionEditor/MarketDate/Confirm/Cancel">
							{t("EDIT_COMPOSITION.REFERENCE_DATE.REPRICE_CONFIRM.CANCEL")}
						</Button>
					}
					primaryAction={
						<SubmitButton data-qualifier="CompositionEditor/MarketDate/Confirm/Apply">
							{t("EDIT_COMPOSITION.REFERENCE_DATE.REPRICE_CONFIRM.CONFIRM")}
						</SubmitButton>
					}
				/>
			}
			onSubmitAsync={() => {
				onConfirm();
			}}
		>
			<Text type="Body/M/Book" as="p" data-qualifier="CompositionEditor/MarketDate/Confirm/Body">
				{t("EDIT_COMPOSITION.REFERENCE_DATE.REPRICE_CONFIRM.BODY", { date: formatDate(date) })}
			</Text>
		</Dialog>
	);
}

/** Resolves `true` when the user confirms the date change, `false` on cancel or dismissal. */
export function spawnRepriceConfirmDialog(props: Pick<RepriceConfirmDialogProps, "date">): SpawnResult<boolean> {
	return spawn<boolean>(
		adaptAnimatedNodeProvider(({ show, resolve, onHidden }) => (
			<RepriceConfirmDialog
				{...props}
				show={show}
				onAnimationStateChange={(state) => state === "hidden" && onHidden()}
				onClose={() => resolve(false)}
				onConfirm={() => resolve(true)}
			/>
		)),
	);
}
